import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { cn } from 'helpers/classnames';
import type CarouselControlsProps from 'tailwind/components/CarouselControls/CarouselControls.types';
import useMartyContext from 'hooks/useMartyContext';
import CaretRightIcon from 'components/common/IconsV2/CaretRightIcon';
import CaretLeftIcon from 'components/common/IconsV2/CaretLeftIcon';

const calculateDotsTransform = (selectedIndex: number) => {
  const indexToStartPagination = 3;
  const dotWidth = 14;
  return `translateX(-${Math.max(0, (selectedIndex - indexToStartPagination) * dotWidth)}px)`;
};

const DotsPaginationWrapper = ({ children, selectedIndex, isActive }: { children: React.ReactNode; selectedIndex: number; isActive: boolean }) => {
  if (!isActive) {
    return children;
  }

  return (
    <div className="transition-transform delay-200 ease-linear" style={{ transform: calculateDotsTransform(selectedIndex) }}>
      {children}
    </div>
  );
};

const paginationButtonClasses = cn(
  'shadow-[4px_8px_8px_0px_rgba(0,0,0,0.04)] transition-colors rounded-sm bg-gray-0 px-0 py-[10px]', // base
  'enabled:hover:bg-gray-900 enabled:hover:text-gray-0', // hover
  'enabled:active:shadow-none enabled:active:bg-gray-500 enabled:active:text-gray-0', // active
  'disabled:cursor-not-allowed' // disabled
);

const CarouselControls = (props: CarouselControlsProps) => {
  const { emblaApi, isAutoHidden = true, forwardButtonOnClick } = props;
  const { testId } = useMartyContext();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (emblaApi) {
        emblaApi.scrollNext();
        forwardButtonOnClick?.(event);
      }
    },
    [emblaApi, forwardButtonOnClick]
  );

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    onInit();
    onSelect();
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  if (isAutoHidden && scrollSnaps.length === 1) {
    // do not show controls if theres only 1 page
    return null;
  }

  return (
    <div className="flex items-center justify-center" data-test-id={testId('carouselControls')}>
      <button
        aria-label="Previous"
        onClick={scrollPrev}
        disabled={prevBtnDisabled}
        data-test-id={testId('carouselControlsLeftArrow')}
        className={cn(paginationButtonClasses, 'mr-[30px] md:mr-[60px]', { ['bg-button-disabled-subtle']: prevBtnDisabled })}
        type="button"
      >
        <CaretLeftIcon className={cn({ ['text-button-disabled-minimal']: prevBtnDisabled })} size={32} />
      </button>
      <div className={'max-w-[100px] overflow-hidden whitespace-nowrap md:max-w-full'}>
        <DotsPaginationWrapper selectedIndex={selectedIndex} isActive>
          {scrollSnaps.map((_, index) => (
            <button
              type="button"
              key={index}
              aria-label={`Page ${index + 1}`}
              onClick={() => scrollTo(index)}
              className={cn('mx-[4px] my-0 h-[6px] cursor-pointer rounded-full border-none bg-button-disabled-subtle p-0', {
                ['w-[20px] rounded-2xl bg-button-selected']: index === selectedIndex,
                ['w-[6px]']: index !== selectedIndex
              })}
              data-test-id={testId('carouselPageDot')}
            />
          ))}
        </DotsPaginationWrapper>
      </div>
      <button
        aria-label="Next"
        onClick={scrollNext}
        disabled={nextBtnDisabled}
        data-test-id={testId('carouselControlsRightArrow')}
        className={cn(paginationButtonClasses, 'ml-[30px] md:ml-[60px]', {
          ['bg-button-disabled-subtle']: nextBtnDisabled
        })}
        type="button"
      >
        <CaretRightIcon className={cn({ ['text-button-disabled-minimal']: nextBtnDisabled })} size={32} />
      </button>
    </div>
  );
};

export default CarouselControls;
